/**
 * SiteOfCareInputField - Input component for sites_of_care array field
 *
 * Manages a list of site of care items with full address and provider information.
 *
 * Exports:
 * - SiteOfCareInputField: Main input component
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { SearchableSelect } from '@components/ui/searchable-select';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import type { SchemaOption } from '@/utils/fields';

import { SchemaProvider, useSchemaContextSafe } from './context/SchemaContext';
import type { InputArrayProps } from './types';

// Path prefix used by the schema for the sites_of_care array's subfields.
// e.g. the schema marks "sites_of_care.name" with validation.required = true.
const SITES_OF_CARE_PATH_PREFIX = 'sites_of_care';

/** Renders the red `*` indicator when the schema marks the field required. */
const RequiredAsterisk: React.FC<{ required: boolean }> = ({ required }) =>
  required ? <span className="text-destructive ml-1">*</span> : null;

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fallback state options, mirroring the `State` enum in
 * `nomos/ui/medbv/v1/enums.proto` (all 59 values, UNSPECIFIED excluded).
 *
 * The dropdown prefers the schema's own options for `sites_of_care.state`;
 * this list only covers the render before the schema resolves, or a mount
 * outside a SchemaProvider (isolated tests). Keep it in sync with the proto,
 * or better, let the schema answer.
 */
const US_STATES_FALLBACK: SchemaOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District Of Columbia' },
  { value: 'FM', label: 'Federated States Of Micronesia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'GU', label: 'Guam' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MH', label: 'Marshall Islands' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PW', label: 'Palau' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VI', label: 'Virgin Islands' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// =============================================================================
// TYPES
// =============================================================================

/**
 * Site of care item shape (defined locally, schema provides field definitions at runtime)
 */
interface SiteOfCareItem {
  name: string;
  npi: string;
  tax_id: string;
  address: string;
  address_line?: string;
  city: string;
  state: string;
  zip: string;
  alternate_names?: string;
}

// =============================================================================
// MAIN INPUT COMPONENT
// =============================================================================

/**
 * Create an empty site of care item
 */
const createEmptyItem = (): SiteOfCareItem => ({
  name: '',
  npi: '',
  tax_id: '',
  address: '',
  address_line: '',
  city: '',
  state: '',
  zip: '',
  alternate_names: '',
});

/**
 * Public component — mounts SchemaProvider so the inner body can consume
 * `isFieldRequired` from context. The inner body MUST be a separate component;
 * calling useSchemaContextSafe() in the same component that renders
 * <SchemaProvider> would see the outer (missing) context, not the one it
 * just installed.
 */
export const SiteOfCareInputField: React.FC<InputArrayProps> = props => (
  <SchemaProvider>
    <SiteOfCareInputFieldInner {...props} />
  </SchemaProvider>
);

const SiteOfCareInputFieldInner: React.FC<InputArrayProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  // Drive the `*` indicator from the schema instead of hardcoding which
  // subfields are required — the schema is the source of truth and varies
  // per workflow. Safe variant so the component still renders correctly if
  // mounted outside a SchemaProvider (e.g. in isolated tests).
  const { isFieldRequired, getFieldOptions } = useSchemaContextSafe();
  const fieldRequired = (subfieldId: string) =>
    isFieldRequired(subfieldId, SITES_OF_CARE_PATH_PREFIX);

  // Cast data to expected shape (schema provides field definitions at runtime)
  const items = (Array.isArray(data) ? data : []) as SiteOfCareItem[];

  const handleAddItem = () => {
    const newItem = createEmptyItem();
    const newItems = [...items, newItem];
    onChange(newItems);
    // Auto-expand the newly added item
    setExpandedItems(prev => [...prev, `item-${items.length}`]);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    setDeleteConfirmIndex(null);
  };

  const handleItemChange = (index: number, field: keyof SiteOfCareItem, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange(newItems);
  };

  const getItemLabel = (item: SiteOfCareItem, index: number): string => {
    if (item.name) return item.name;
    return `Site ${index + 1}`;
  };

  // Resolve the state dropdown from the schema, the same source every other
  // enum in this module reads — so the list can't drift from the proto enum
  // (a hardcoded list was missing 8 US territories). Falls back to the local
  // mirror only while the schema is still loading.
  const schemaStateOptions = getFieldOptions('state', SITES_OF_CARE_PATH_PREFIX);
  const stateOptions = schemaStateOptions.length > 0 ? schemaStateOptions : US_STATES_FALLBACK;

  if (items.length === 0) {
    return (
      <div className="py-2">
        <div className="text-sm text-muted-foreground italic mb-2">No sites of care added</div>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Site of Care
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <Accordion
        type="multiple"
        value={expandedItems}
        onValueChange={setExpandedItems}
        className="space-y-1"
      >
        {items.map((item, index) => {
          const itemKey = `item-${index}`;
          const itemLabel = getItemLabel(item, index);

          return (
            <AccordionItem key={itemKey} value={itemKey} className="border rounded-md bg-muted/30">
              <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                <div className="flex items-center gap-2 flex-1">
                  <span>{itemLabel}</span>
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteConfirmIndex(index);
                    }}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div className="space-y-3">
                  {/* Name */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      Facility Name
                      <RequiredAsterisk required={fieldRequired('name')} />
                    </label>
                    <Input
                      type="text"
                      value={item.name || ''}
                      onChange={e => handleItemChange(index, 'name', e.target.value)}
                      placeholder="e.g., Austin Medical Center"
                      disabled={disabled}
                      className={`h-8 text-sm ${errors[`${index}.name`] ? 'border-destructive' : ''}`}
                    />
                    {errors[`${index}.name`] && (
                      <div className="text-xs text-destructive mt-1">{errors[`${index}.name`]}</div>
                    )}
                  </div>

                  {/* NPI and Tax ID row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        NPI
                        <RequiredAsterisk required={fieldRequired('npi')} />
                      </label>
                      <Input
                        type="text"
                        value={item.npi || ''}
                        onChange={e => handleItemChange(index, 'npi', e.target.value)}
                        placeholder="10-digit NPI"
                        disabled={disabled}
                        className={`h-8 text-sm ${errors[`${index}.npi`] ? 'border-destructive' : ''}`}
                      />
                      {errors[`${index}.npi`] && (
                        <div className="text-xs text-destructive mt-1">
                          {errors[`${index}.npi`]}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Tax ID</label>
                      <Input
                        type="text"
                        value={item.tax_id || ''}
                        onChange={e => handleItemChange(index, 'tax_id', e.target.value)}
                        placeholder="XX-XXXXXXX"
                        disabled={disabled}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Address</label>
                    <Input
                      type="text"
                      value={item.address || ''}
                      onChange={e => handleItemChange(index, 'address', e.target.value)}
                      placeholder="Street address"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      Address Line 2
                    </label>
                    <Input
                      type="text"
                      value={item.address_line || ''}
                      onChange={e => handleItemChange(index, 'address_line', e.target.value)}
                      placeholder="Suite, unit, floor, etc."
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* City, State, Zip row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        City
                        <RequiredAsterisk required={fieldRequired('city')} />
                      </label>
                      <Input
                        type="text"
                        value={item.city || ''}
                        onChange={e => handleItemChange(index, 'city', e.target.value)}
                        placeholder="City"
                        disabled={disabled}
                        className={`h-8 text-sm ${errors[`${index}.city`] ? 'border-destructive' : ''}`}
                      />
                      {errors[`${index}.city`] && (
                        <div className="text-xs text-destructive mt-1">
                          {errors[`${index}.city`]}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        State
                        <RequiredAsterisk required={fieldRequired('state')} />
                      </label>
                      <SearchableSelect
                        id={`site-${index}-state`}
                        value={item.state || ''}
                        onValueChange={value => handleItemChange(index, 'state', String(value))}
                        options={stateOptions}
                        placeholder="Select"
                        disabled={disabled}
                        error={!!errors[`${index}.state`]}
                        className="h-8 text-sm"
                      />
                      {errors[`${index}.state`] && (
                        <div className="text-xs text-destructive mt-1">
                          {errors[`${index}.state`]}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        ZIP
                        <RequiredAsterisk required={fieldRequired('zip')} />
                      </label>
                      <Input
                        type="text"
                        value={item.zip || ''}
                        onChange={e => handleItemChange(index, 'zip', e.target.value)}
                        placeholder="ZIP code"
                        disabled={disabled}
                        className={`h-8 text-sm ${errors[`${index}.zip`] ? 'border-destructive' : ''}`}
                      />
                      {errors[`${index}.zip`] && (
                        <div className="text-xs text-destructive mt-1">
                          {errors[`${index}.zip`]}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Alternate Names */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      Alternate Names
                    </label>
                    <Input
                      type="text"
                      value={item.alternate_names || ''}
                      onChange={e => handleItemChange(index, 'alternate_names', e.target.value)}
                      placeholder="Other names this facility is known by"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Add button */}
      {!disabled && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Site of Care
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmIndex !== null}
        onOpenChange={open => !open && setDeleteConfirmIndex(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Site of Care?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete this site of care and all
              its data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmIndex !== null && handleDeleteItem(deleteConfirmIndex)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
